import { useEffect, useState } from "react";
import { Heart, MessageCircle, Pin, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserAvatar";
import { PostPhotos } from "@/components/PostPhotos";
import { VideoEmbed } from "@/components/VideoEmbed";

const Comments = ({ post, onCount }) => {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");

  useEffect(() => {
    api.get(`/posts/${post.id}/comments`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [post.id]);

  if (items === null) {
    return <div className="text-sm text-[var(--hb-muted)] pt-4">Loading comments...</div>;
  }

  const send = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/posts/${post.id}/comments`, { body: text });
      setItems([...items, data]);
      onCount(1);
      setText("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (c) => {
    try {
      await api.delete(`/comments/${c.id}`);
      setItems(items.filter((x) => x.id !== c.id));
      onCount(-1);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="pt-4 mt-4 border-t border-[var(--hb-line)] space-y-4" data-testid={`comments-${post.id}`}>
      {items.map((c) => (
        <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}>
          <UserAvatar user={c.author} size={30} />
          <div className="flex-1 bg-[#F5F5F0] rounded-2xl px-4 py-2.5">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold">{c.author.name}</span>
              <span className="text-[var(--hb-muted)] flex items-center gap-2">
                {timeAgo(c.created_at)}
                {(c.author.id === user.id || user.role === "admin") && (
                  <button onClick={() => remove(c)} data-testid={`comment-delete-${c.id}`} className="hover:text-red-600">
                    <Trash2 size={12} />
                  </button>
                )}
              </span>
            </div>
            <p className="text-sm mt-1 whitespace-pre-wrap">{c.body}</p>
          </div>
        </div>
      ))}
      <form onSubmit={send} className="flex gap-2">
        <input className="hb-input !py-2" placeholder="Add a comment..." value={text}
          onChange={(e) => setText(e.target.value)} data-testid={`comment-input-${post.id}`} />
        <button className="btn-primary !px-3" disabled={!text.trim()} data-testid={`comment-submit-${post.id}`}>
          <Send size={14} />
        </button>
      </form>
    </div>
  );
};

export const PostCard = ({ post, onChange, onDelete }) => {
  const { user } = useAuth();
  const [showComments, setShowComments] = useState(false);
  const canDelete = post.author.id === user.id || user.role === "admin";

  const safe = (fn) => async () => {
    try {
      await fn();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const like = safe(async () => onChange((await api.post(`/posts/${post.id}/like`)).data));
  const pin = safe(async () => onChange((await api.post(`/posts/${post.id}/pin`)).data));
  const remove = safe(async () => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/posts/${post.id}`);
    onDelete(post.id);
  });

  return (
    <article className="hb-card p-5 md:p-6 rise" data-testid={`post-${post.id}`}>
      <div className="flex items-start gap-3">
        <UserAvatar user={post.author} size={42} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 text-sm">
            <span className="font-semibold">{post.author.name}</span>
            {post.author.role === "admin" && (
              <span className="label-accent !text-[9px] bg-[var(--hb-ink)] !text-[var(--hb-lime)] px-1.5 py-0.5 rounded">Host</span>
            )}
          </div>
          <div className="text-xs text-[var(--hb-muted)]">
            {timeAgo(post.created_at)} · <span className="text-[var(--hb-ink)]">{post.category}</span>
          </div>
        </div>
        {post.pinned && (
          <span className="flex items-center gap-1 label-accent !text-[10px] bg-[var(--hb-lime-soft)] !text-[var(--hb-ink)] px-2 py-1 rounded-full" data-testid={`post-pinned-badge-${post.id}`}>
            <Pin size={11} /> Pinned
          </span>
        )}
      </div>
      {post.title && <h3 className="font-display text-xl mt-4">{post.title}</h3>}
      {post.body && <p className="mt-2 whitespace-pre-wrap leading-relaxed text-[15px]">{post.body}</p>}
      <VideoEmbed video={post.video} testId={`post-video-${post.id}`} />
      <PostPhotos postId={post.id} images={post.images} />
      <div className="flex items-center gap-1 mt-5 -ml-2 text-sm">
        <button onClick={like} data-testid={`post-like-${post.id}`}
          className={`flex items-center gap-1.5 px-2 py-1.5 rounded-full hover:bg-[var(--hb-lime-soft)] transition-colors ${post.liked ? "text-[var(--hb-ink)] font-semibold" : "text-[var(--hb-muted)]"}`}>
          <Heart size={16} className={post.liked ? "fill-[var(--hb-lime)] stroke-[var(--hb-ink)]" : ""} />
          <span data-testid={`post-like-count-${post.id}`}>{post.like_count}</span>
        </button>
        <button onClick={() => setShowComments(!showComments)} data-testid={`post-comments-toggle-${post.id}`}
          className="flex items-center gap-1.5 px-2 py-1.5 rounded-full text-[var(--hb-muted)] hover:bg-[var(--hb-lime-soft)] transition-colors">
          <MessageCircle size={16} /> <span data-testid={`post-comment-count-${post.id}`}>{post.comment_count}</span>
        </button>
        <div className="flex-1" />
        {user.role === "admin" && (
          <button onClick={pin} data-testid={`post-pin-${post.id}`} title={post.pinned ? "Unpin" : "Pin"}
            className="p-2 rounded-full text-[var(--hb-muted)] hover:text-[var(--hb-ink)] hover:bg-[#F5F5F0]">
            <Pin size={15} />
          </button>
        )}
        {canDelete && (
          <button onClick={remove} data-testid={`post-delete-${post.id}`}
            className="p-2 rounded-full text-[var(--hb-muted)] hover:text-red-600 hover:bg-red-50">
            <Trash2 size={15} />
          </button>
        )}
      </div>
      {showComments && (
        <Comments post={post} onCount={(d) => onChange({ ...post, comment_count: post.comment_count + d })} />
      )}
    </article>
  );
};
